import api from './api';

/**
 * App Device Service
 * Manages mobile/desktop VPN app devices (V2 model)
 * Uses /api/v2/wireguard/apps/* endpoints
 */

// App Device types
export interface AppDevice {
  id: string;
  name: string;
  platform: 'iOS' | 'Android' | 'Windows' | 'macOS' | 'Linux';
  osVersion?: string;
  appVersion?: string;
  status: 'active' | 'inactive' | 'suspended' | 'revoked';
  lastSeenAt?: string;
  deviceGroupId?: string | null;
  createdAt: string;
  /** "wireguard" | "ikev2"; "access" for a device in an Access group (with plane "v3"). */
  tunnelProtocol?: string;
  /** "v3" for a device in an Access group (VSNP-1626); absent for a v2 device. */
  plane?: 'v3';
  peer?: AppPeer | null;
  /** Access-plane devices only: the live view from Secure Access (VSNP-1626 W.6). */
  access?: AccessLiveView;
}

/**
 * What Secure Access knows about an Access-plane device right now (VSNP-1626
 * W.6): the API reads it from access_devices and the open session, and sets
 * the device's `status` ("active" while connected) and `lastSeenAt` from it.
 * Absent for a v2 device and on an older API.
 */
export interface AccessLiveView {
  /** The Access device's own status in the app vocabulary (e.g. "active", "pending_approval"). */
  deviceStatus: string;
  /** It has an open session on a gateway that is not offline. */
  connected: boolean;
  gatewayId: string | null;
  gatewayName: string | null;
  /** When the open session started. */
  connectedAt: string | null;
}

export interface AppPeer {
  id: string;
  edgeId: string;
  edgeName?: string;
  assignedIp: string;
  lastHandshakeAt?: string;
  rxBytes?: number;
  txBytes?: number;
  connectedAt?: string;
}

export interface ConnectionHistoryEvent {
  id: string;
  eventType: 'connected' | 'disconnected';
  eventAt: string;
  assignedIp?: string | null;
  endpoint?: string | null;
  rxBytes: number;
  txBytes: number;
  disconnectReason?: string | null;
}

export interface AppDeviceDetail {
  device: {
    id: string;
    name: string;
    platform: string;
    osVersion?: string;
    appVersion?: string;
    fingerprint: string;
    ownerId: string;
    ownerEmail?: string;
    ownerName?: string;
    status: string;
    lastSeenAt?: string;
    deviceGroupId?: string | null;
    createdAt: string;
    updatedAt?: string;
    /** "wireguard" | "ikev2"; "access" for a device in an Access group (with plane "v3"). */
    tunnelProtocol?: string;
    /** "v3" for a device in an Access group (VSNP-1626); absent for a v2 device. */
    plane?: 'v3';
    /** Access-plane devices only: the live view from Secure Access (VSNP-1626 W.6). */
    access?: AccessLiveView;
  };
  peers: {
    id: string;
    edgeId: string;
    edgeName?: string;
    assignedIp: string;
    allowedIps: string[];
    status: string;
    enabled: boolean;
    lastHandshakeAt?: string;
    rxBytes?: number;
    txBytes?: number;
    connectedAt?: string;
    disconnectedAt?: string;
    createdAt: string;
  }[];
  connectionHistory?: ConnectionHistoryEvent[];
}

/**
 * A device group's anchor (VSNP-1098): a dedicated edge or an MTGE (the v2
 * plane: WireGuard / IKEv2), or an Access Gateway (the v3 plane, ZTNA). For
 * 'access', edge_id is null (any gateway assigned to the tenant) or an
 * Access Gateway id.
 */
export type DeviceGroupEdgeType = 'dedicated' | 'mtge' | 'access';

/**
 * An Access group's connect policy (doc 28 §4.9, revised 2026-10-02). The v3
 * tunnel is always on — there is no manual or on-demand mode and no trusted
 * network. `lockdown` (the kill switch, default on) blocks everything but the
 * gateways and sign-in while the tunnel is down, and needs a full tunnel;
 * `failMode` says what lockdown does when the tunnel cannot come up (closed:
 * keep blocking; open: release); `userCanDisconnect` shows a Disconnect
 * button. `lockdown` absent = on for a full tunnel, off for a split one;
 * `failMode` absent = the tenant's default: open when the tenant signs in
 * through an external IdP, closed otherwise (option A, 2026-10-07).
 */
export interface AccessConnectPolicy {
  lockdown?: boolean;
  failMode?: 'closed' | 'open';
  userCanDisconnect: boolean;
}

/**
 * The connect policy as the group's devices receive it (the API resolves it;
 * Access groups only). `failModeReason: 'external_idp'`: the group is set to
 * fail closed, but the tenant signs in through an external IdP, which a
 * locked-down device cannot reach, so its devices fail open (doc 28 §4.9,
 * option A of 2026-10-07).
 */
export interface EffectiveAccessConnect {
  lockdown: boolean;
  failMode: 'closed' | 'open';
  failModeReason?: 'external_idp';
  userCanDisconnect: boolean;
}

/**
 * A group's override of the sign-in lifetime policy (doc 28 §4.10), seconds.
 * An absent field takes the tenant value; `{}` clears the override.
 */
export interface AccessSignInOverride {
  reauthInterval?: number;
  tokenLifetime?: number;
}

/** Tunnel settings an Access (v3) group hands its clients. */
export interface AccessTunnelSettings {
  mode: 'full' | 'split';
  routes: string[];
  dnsServers: string[];
  dnsMatchDomains: string[];
  ipv6: 'block' | 'tunnel';
  /** Absent: the defaults (and, on an update, the stored policy is kept). */
  connect?: AccessConnectPolicy;
  /** Absent: the tenant's values (and, on an update, the stored override is kept). */
  signIn?: AccessSignInOverride;
}

export interface DeviceGroup {
  id: string;
  name: string;
  description?: string;
  subnet_cidr?: string;
  edge_id?: string | null;
  edge_type?: DeviceGroupEdgeType;
  max_devices?: number;
  device_count?: number;
  routing_policy_ids?: string[];
  dns_visibility?: 'all' | 'specific';
  dns_record_count?: number;
  dns_record_ids?: string[];
  access_tunnel?: AccessTunnelSettings | null;
  /** Access groups only: the policy the group's devices receive. */
  access_connect?: EffectiveAccessConnect;
  createdAt: string;
}

/** Create / update body for a device group. */
export interface DeviceGroupInput {
  name?: string;
  description?: string;
  tenant_id?: string;
  edge_id?: string;
  edge_type?: DeviceGroupEdgeType;
  max_devices?: number;
  routing_policy_ids?: string[];
  dns_record_ids?: string[];
  access_tunnel?: AccessTunnelSettings | null;
}

export interface RegisterDeviceRequest {
  deviceName: string;
  deviceFingerprint: string;
  platform: 'iOS' | 'Android' | 'Windows' | 'macOS' | 'Linux';
  osVersion?: string;
  appVersion?: string;
  publicKey: string;
  preferredRegion?: string;
}

export interface RegisterDeviceResponse {
  device: {
    id: string;
    name: string;
    platform: string;
    status: string;
  };
  peer: {
    id: string;
    assignedIp: string;
    allowedIps: string[];
    persistentKeepalive: number;
    serverPublicKey: string;
    listenPort: number;
    endpointHost?: string | null;
  };
  edge: {
    id: string;
  };
}

export interface ListDevicesResponse {
  items: AppDevice[];
  total: number;
  tunnelProtocol?: string;
}

export interface ListDeviceGroupsResponse {
  items: DeviceGroup[];
  page: number;
  pageSize: number;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

const appDeviceService = {
  /**
   * Register a new device and create VPN peer
   */
  register: async (data: RegisterDeviceRequest): Promise<RegisterDeviceResponse> => {
    const response = await api.post('/api/v2/wireguard/apps/register', data);
    return response.data;
  },

  /**
   * Get all devices owned by authenticated user
   */
  listMyDevices: async (): Promise<ListDevicesResponse> => {
    const response = await api.get('/api/v2/wireguard/apps/devices/me');
    return response.data;
  },

  /**
   * List all devices in tenant (admin view)
   */
  listAllDevices: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    ownerId?: string;
    ownerEmail?: string;
    groupId?: string;
    tenantId?: string;
  }): Promise<ListDevicesResponse> => {
    const response = await api.get('/api/v2/wireguard/apps/devices', { params });
    return response.data;
  },

  /**
   * Get device details by ID
   */
  getDeviceById: async (id: string): Promise<AppDeviceDetail> => {
    const response = await api.get(`/api/v2/wireguard/apps/devices/${id}`);
    return response.data;
  },

  /**
   * Update device
   */
  updateDevice: async (
    id: string,
    data: {
      deviceName?: string;
      osVersion?: string;
      appVersion?: string;
      status?: 'active' | 'inactive' | 'suspended';
    }
  ) => {
    const response = await api.patch(`/api/v2/wireguard/apps/devices/${id}`, data);
    return response.data;
  },

  /**
   * Delete device and all its peers
   */
  deleteDevice: async (id: string) => {
    await api.delete(`/api/v2/wireguard/apps/devices/${id}`);
  },

  /**
   * Revoke device access — terminates active SA, blocks self-re-registration
   * with this fingerprint until re-enabled. Same endpoint the VPN-User-Detail
   * page uses (see VPNUserDetail.tsx).
   */
  revokeDevice: async (id: string) => {
    await api.post(`/api/v2/wireguard/apps/devices/${id}/revoke`);
  },

  /**
   * Re-enable a revoked device — clears the access block (status → 'active').
   * Cert stays on the CRL; the user must re-register from the app to get a
   * fresh cert and reconnect.
   */
  reenableDevice: async (id: string) => {
    await api.post(`/api/v2/wireguard/apps/devices/${id}/reenable`);
  },

  /**
   * Deregister VPN peer (disconnect)
   */
  deletePeer: async (peerId: string) => {
    await api.delete(`/api/v2/wireguard/apps/peers/${peerId}`);
  },

  /**
   * Rotate WireGuard keys
   */
  rotateKeys: async (peerId: string, newPublicKey: string) => {
    const response = await api.post(`/api/v2/wireguard/apps/peers/${peerId}/rotate`, {
      newPublicKey,
    });
    return response.data;
  },

  /**
   * List device groups
   */
  listDeviceGroups: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<ListDeviceGroupsResponse> => {
    const response = await api.get('/api/v2/wireguard/apps/device-groups', { params });
    return response.data;
  },

  /**
   * Create device group
   */
  createDeviceGroup: async (data: DeviceGroupInput & { name: string }) => {
    const response = await api.post('/api/v2/wireguard/apps/device-groups', data);
    return response.data;
  },

  /**
   * Update device group
   */
  updateDeviceGroup: async (
    id: string,
    data: DeviceGroupInput
  ) => {
    const response = await api.patch(`/api/v2/wireguard/apps/device-groups/${id}`, data);
    return response.data;
  },

  /**
   * Delete device group
   */
  deleteDeviceGroup: async (id: string) => {
    await api.delete(`/api/v2/wireguard/apps/device-groups/${id}`);
  },

  /**
   * Assign device to group
   */
  assignDeviceToGroup: async (deviceId: string, groupId: string) => {
    const response = await api.post(
      `/api/v2/wireguard/apps/devices/${deviceId}/assign-group`,
      { groupId }
    );
    return response.data;
  },

  /**
   * Remove device from group
   */
  removeDeviceFromGroup: async (deviceId: string) => {
    const response = await api.post(
      `/api/v2/wireguard/apps/devices/${deviceId}/remove-group`
    );
    return response.data;
  },

  /**
   * Get failover groups for a DG
   */
  getFailoverGroups: async (groupId: string) => {
    const response = await api.get(`/api/v2/wireguard/apps/device-groups/${groupId}/failovers`);
    return response.data;
  },

  /**
   * Set failover groups for a DG
   */
  setFailoverGroups: async (groupId: string, failoverGroupIds: string[]) => {
    const response = await api.put(
      `/api/v2/wireguard/apps/device-groups/${groupId}/failovers`,
      { failover_group_ids: failoverGroupIds }
    );
    return response.data;
  },

  /**
   * Get routable subnets for a DG's bound edge
   */
  getRoutableSubnets: async (groupId: string) => {
    const response = await api.get(`/api/v2/wireguard/apps/device-groups/${groupId}/routable-subnets`);
    return response.data?.items || [];
  },

  /**
   * Apply a DGAP template to a device group
   */
  applyDgapTemplate: async (groupId: string, templateId: string, ruleOverrides?: Record<number, { destination_subnet?: string }>) => {
    const response = await api.post(
      `/api/v2/wireguard/apps/device-groups/${groupId}/apply-template`,
      { template_id: templateId, rule_overrides: ruleOverrides }
    );
    return response.data;
  },

  /**
   * Get capacity for a DG
   */
  getCapacity: async (groupId: string) => {
    const response = await api.get(`/api/v2/wireguard/apps/device-groups/${groupId}/capacity`);
    return response.data;
  },

  /**
   * Get unbound DG count (for migration banner)
   */
  getUnboundGroupCount: async (tenantId?: string) => {
    const response = await api.get('/api/v2/wireguard/apps/device-groups/unbound-count', {
      params: tenantId ? { tenantId } : undefined,
    });
    return response.data;
  },

  /**
   * Get user's assigned DGs
   */
  getUserDeviceGroups: async (userId: string) => {
    const response = await api.get(`/api/v2/wireguard/apps/users/${userId}/device-groups`);
    return response.data;
  },

  /**
   * Set user's assigned DGs
   */
  setUserDeviceGroups: async (userId: string, deviceGroupIds: string[]) => {
    const response = await api.put(
      `/api/v2/wireguard/apps/users/${userId}/device-groups`,
      { device_group_ids: deviceGroupIds }
    );
    return response.data;
  },

  /**
   * Get MTGEs assigned to current tenant (for DG edge selector)
   */
  getMyMtges: async (tenantId?: string) => {
    const params = tenantId ? `?tenantId=${tenantId}` : '';
    const response = await api.get(`/api/v2/wireguard/apps/my-mtges${params}`);
    return response.data as { items: Array<{ id: string; name: string; wanIp?: string; status: string }> };
  },

  /**
   * Get authenticated user's available DGs (for VPN app)
   */
  getMyGroups: async () => {
    const response = await api.get('/api/v2/wireguard/apps/my-groups');
    return response.data;
  },

  /**
   * Switch device to a different DG
   */
  switchGroup: async (deviceId: string, targetGroupId: string) => {
    const response = await api.post('/api/v2/wireguard/apps/switch-group', {
      deviceId,
      targetGroupId,
    });
    return response.data;
  },

  /**
   * Remove access policy from a device group
   */
  removeAccessPolicy: async (groupId: string) => {
    const response = await api.delete(`/api/v2/wireguard/apps/device-groups/${groupId}/access-policy`);
    return response.data;
  },
};

export default appDeviceService;
